import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Globe, Handshake, Building2, Users, 
  Briefcase, TrendingUp, Shield, Zap, CheckCircle2,
  Mail, Phone, Send, Loader2, Award, Target,
  CreditCard, PieChart, HeadphonesIcon, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function PartnershipsPage() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    companyType: '',
    partnershipType: '',
    message: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.companyName || !formData.contactName || !formData.email || !formData.companyType || !formData.partnershipType) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(API + '/partnerships/inquiry', formData);
      setSubmitted(true);
      toast.success('Partnership inquiry submitted successfully!');
    } catch (err) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const partnershipBenefits = [
    { icon: Shield, title: 'Co-Development', desc: 'Build custom solutions for other visa categories' },
    { icon: CreditCard, title: 'Bulk AI Credits', desc: 'Purchase AI credits at discounted rates for your clients' },
    { icon: Building2, title: 'White-Label Solutions', desc: 'Offer our platform under your own brand' },
    { icon: Users, title: 'Client Referrals', desc: 'Earn commissions for every client you refer' },
    { icon: HeadphonesIcon, title: 'Priority Support', desc: 'Dedicated account manager for your firm' },
    { icon: PieChart, title: 'Analytics Dashboard', desc: 'Track your clients progress and usage' }
  ];

  const idealPartners = [
    'Immigration Consultants',
    'Immigration Law Firms',
    'Visa Advisory Agencies',
    'Business Setup Consultancies',
    'Relocation Services',
    'Corporate Immigration Teams'
  ];

  return (
    <div className="min-h-screen bg-silver-gradient text-slate-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-header">
        <div className="container-wide py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl gradient-red flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight">
                <span className="text-slate-900">innovatorfounder</span>
                <span className="text-gradient-red">visas</span>
              </span>
            </Link>
            
            <Link to="/">
              <Button variant="ghost" className="btn-ghost">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-10 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-slate-200/50 rounded-full blur-3xl" />
        </div>

        <div className="container-wide relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <Badge className="badge-red mb-6 px-4 py-2">
              <Handshake className="w-4 h-4 mr-2 inline" />
              B2B Partnership Program
            </Badge>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 mb-6 leading-tight">
              Partner With Us to
              <span className="block text-gradient-red">
                Transform Immigration Services
              </span>
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Are you an immigration consultant, law firm, or agency? Join our partnership program 
              and leverage cutting-edge AI technology to better serve your clients.
            </p>

            <div className="flex flex-wrap justify-center gap-4">
              <a href="#partnership-form">
                <Button className="btn-primary text-lg px-8 py-4">
                  Become a Partner
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who Should Partner Section */}
      <section className="py-20 bg-slate-100">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Ideal Partnership For
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We work with professionals and organizations in the immigration industry
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {idealPartners.map((partner, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-xl p-4 text-center border border-slate-200 hover:border-red-500/50 hover:shadow-lg transition-all"
              >
                <CheckCircle2 className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-slate-800 text-sm font-medium">{partner}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Benefits */}
      <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-red-50/30">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <Badge className="badge-red mb-4">
              Partnership Benefits
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Why Partner With Us?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Gain access to exclusive benefits designed to help your firm grow and serve clients better
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {partnershipBenefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-lg hover:shadow-xl hover:border-red-200 transition-all group"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-red-500/25">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{benefit.title}</h3>
                <p className="text-slate-600">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Form Section */}
      <section id="partnership-form" className="py-20 bg-white">
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left - Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="badge-red mb-4">
                Get Started Today
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Let's Build Something Great Together
              </h2>
              <p className="text-slate-600 text-lg mb-8 leading-relaxed">
                Fill out the form and our partnership team will get back to you within 24-48 hours 
                to discuss how we can work together.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-red-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Tailored Solutions</h4>
                    <p className="text-slate-500 text-sm">We customize our partnership model to fit your business needs</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Proven Success</h4>
                    <p className="text-slate-500 text-sm">94% success rate for clients using our platform</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-blue-500" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Quick Onboarding</h4>
                    <p className="text-slate-500 text-sm">Get started within days, not months</p>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="mt-10 p-6 bg-slate-50 rounded-2xl border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-4">Direct Contact</h4>
                <div className="space-y-3">
                  <a href="https://wa.me/447380885506" className="flex items-center gap-3 text-slate-600 hover:text-green-500 transition-colors">
                    <Phone className="w-5 h-5" />
                    <span>+44 7380 885506 (WhatsApp)</span>
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Right - Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {submitted ? (
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-10 border border-green-200 text-center">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">Thank You!</h3>
                  <p className="text-slate-600 mb-6">
                    Your partnership inquiry has been submitted successfully. 
                    We'll review your information and get back to you within 24-48 hours.
                  </p>
                  <Button 
                    onClick={() => { setSubmitted(false); setFormData({ companyName: '', contactName: '', email: '', phone: '', companyType: '', partnershipType: '', message: '' }); }}
                    variant="outline"
                    className="border-green-500 text-green-600 hover:bg-green-50"
                  >
                    Submit Another Inquiry
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-xl border border-slate-100" data-testid="partnership-form">
                  <h3 className="text-2xl font-bold text-slate-900 mb-6">Partnership Inquiry</h3>
                  
                  <div className="space-y-5">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Company/Firm Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleChange}
                          placeholder="Your Company Ltd"
                          className="w-full"
                          required
                          data-testid="partnership-company-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Contact Person <span className="text-red-500">*</span>
                        </label>
                        <Input
                          name="contactName"
                          value={formData.contactName}
                          onChange={handleChange}
                          placeholder="John Smith"
                          className="w-full"
                          required
                          data-testid="partnership-contact-name"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@company.com"
                          className="w-full"
                          required
                          data-testid="partnership-email"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Phone Number
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+44 123 456 7890"
                          className="w-full"
                          data-testid="partnership-phone"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Type of Organization <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="companyType"
                        value={formData.companyType}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white text-slate-900"
                        required
                        data-testid="partnership-company-type"
                      >
                        <option value="">Select your organization type</option>
                        <option value="immigration_consultant">Immigration Consultant</option>
                        <option value="law_firm">Immigration Law Firm</option>
                        <option value="visa_agency">Visa Advisory Agency</option>
                        <option value="business_consultancy">Business Setup Consultancy</option>
                        <option value="relocation_service">Relocation Service</option>
                        <option value="corporate_immigration">Corporate Immigration Team</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Partnership Interest <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="partnershipType"
                        value={formData.partnershipType}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white text-slate-900"
                        required
                        data-testid="partnership-type"
                      >
                        <option value="">What interests you most?</option>
                        <option value="bulk_credits">Bulk AI Credits Purchase</option>
                        <option value="white_label">White-Label Solution</option>
                        <option value="referral_program">Client Referral Program</option>
                        <option value="co_development">Co-Development for Other Visas</option>
                        <option value="reseller">Reseller Partnership</option>
                        <option value="custom">Custom Partnership Model</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Tell Us More About Your Needs
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Describe your business, number of clients you serve monthly, and how you envision this partnership working..."
                        rows={4}
                        className="w-full"
                        data-testid="partnership-message"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full btn-primary py-3"
                      disabled={loading}
                      data-testid="partnership-submit"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Submit Partnership Inquiry
                        </>
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-slate-500 mt-4 text-center">
                    By submitting, you agree to our Terms of Service and Privacy Policy
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-12">
        <div className="container-wide">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl gradient-red flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white">innovatorfoundervisas.com</span>
            </div>
            <p className="text-slate-500 text-sm text-center">
              © 2026 innovatorfoundervisas.com. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default PartnershipsPage;
